import type { HTMLAttributes, ReactElement, ReactNode } from 'react'
import clsx from 'clsx'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import Caption from 'LuxKit/Typography/Caption'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import BasePill, { type _BasePillKind, type _BasePillVariant } from './BasePill'

export type PillPalette = 'default' | 'success' | 'lux-plus'

const paletteStyles = css`
  &:where(.palette-lux-plus) {
    --base-pill-background: var(--palette-product-lux-plus-background);
    --base-pill-foreground: var(--palette-product-lux-plus-foreground);
    --base-pill-contrast: var(--palette-product-lux-plus-contrast);
  }
`

const legacyPaletteStyles = css`
  &:where(.palette-default) {
    --base-pill-background: var(--palette-highlight-primary-light-background);
    --base-pill-foreground: var(--palette-highlight-primary-light-foreground);
    --base-pill-contrast: var(--palette-highlight-primary-light-contrast);
  }

  &:where(.palette-success) {
    --base-pill-background: var(--palette-highlight-primary-normal-background);
    --base-pill-foreground: var(--palette-highlight-primary-normal-foreground);
    --base-pill-contrast: var(--palette-highlight-primary-normal-contrast);
  }
`

const rebrandPaletteStyles = css`
  &:where(.palette-default) {
    --base-pill-background: var(--palette-highlight-primary-normal-background);
    --base-pill-foreground: var(--palette-highlight-primary-normal-foreground);
    --base-pill-contrast: var(--palette-highlight-primary-normal-contrast);
  }

  &:where(.palette-success) {
    --base-pill-background: var(--palette-highlight-primary-normal-background);
    --base-pill-foreground: var(--palette-highlight-primary-normal-foreground);
    --base-pill-contrast: var(--palette-highlight-primary-normal-contrast);
  }
`

const StyledBasePill = styled(BasePill)`
  --pill-height: ${rem(24)};
  --pill-padding-inline: ${rem(8)};
  --pill-icon-size: ${rem(16)};
  --pill-icon-gap: ${rem(4)};
  /* Keep the transparent base colour explicit for future color-mix() usage. */
  --base-pill-transparent: rgb(255 255 255 / 0%);

  height: var(--pill-height);
  padding-inline: var(--pill-padding-inline);
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex-wrap: nowrap;
  border-radius: var(--border-radius-round);

  > svg:first-of-type,
  > svg:last-of-type {
    flex-shrink: 0;
    height: var(--pill-icon-size);
    width: var(--pill-icon-size);
  }

  &.has-icon-start > :first-child {
    margin-inline-end: var(--pill-icon-gap);
  }

  &.has-icon-end > :last-child {
    margin-inline-start: var(--pill-icon-gap);
  }

  ${paletteStyles}

  :where(body:not(.${REBRAND_PALETTE_CLASS})) & {
    ${legacyPaletteStyles}
  }

  :where(body.${REBRAND_PALETTE_CLASS}) & {
    ${rebrandPaletteStyles}
  }
`

interface Props extends HTMLAttributes<HTMLSpanElement>, Utils.PropsWithDataAttributes {
  children?: ReactNode
  kind?: _BasePillKind
  variant?: _BasePillVariant
  palette?: PillPalette
  startIcon?: ReactElement | false
  endIcon?: ReactElement | false
}

function Pill({
  children,
  className,
  endIcon,
  kind = 'primary',
  palette = 'default',
  startIcon,
  variant = 'default',
  ...props
}: Props) {
  const hasStartIcon = !!startIcon
  const hasEndIcon = !!endIcon

  return <StyledBasePill
    {...props}
    className={clsx(className, `palette-${palette}`, {
      'has-icon-start': hasStartIcon,
      'has-icon-end': hasEndIcon,
    })}
    kind={kind}
    variant={variant}
  >
    {startIcon}
    <Caption variant="medium" weight="bold">{children}</Caption>
    {endIcon}
  </StyledBasePill>
}

export default Pill
